import { apiFetch } from './apiFetch';
import type {
  CandidateReport,
  RedactionCategory,
  RedactionCategoryInfo,
  RedactionManifest,
  RedactionSelectionPayload,
} from '../types/redaction';

/**
 * An error the reviewer needs explained rather than retried.
 *
 * ``reason`` distinguishes a record that cannot be redacted at all -- an
 * unsupported format -- from a transient failure, so the interface can say
 * which it is instead of implying the reviewer lacks permission.
 */
export class RedactionRequestError extends Error {
  readonly status: number;
  readonly reason: string | null;

  constructor(message: string, status: number, reason: string | null) {
    super(message);
    this.name = 'RedactionRequestError';
    this.status = status;
    this.reason = reason;
  }
}

async function toError(response: Response): Promise<RedactionRequestError> {
  const body = await response.json().catch(() => ({}));
  return new RedactionRequestError(
    body.error || 'The request failed',
    response.status,
    body.reason ?? null,
  );
}

/** The categories the API can search for, with their labels and descriptions. */
export async function fetchRedactionCategories(
  href: string,
): Promise<RedactionCategoryInfo[]> {
  const response = await apiFetch(href);
  if (!response.ok) throw await toError(response);

  const payload = await response.json();
  return (payload._embedded?.categories ?? []) as RedactionCategoryInfo[];
}

export async function fetchRedactionCandidates(
  href: string,
  options: { categories: RedactionCategory[]; terms: string[] },
): Promise<CandidateReport> {
  const response = await apiFetch(href, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      categories: options.categories,
      terms: options.terms,
    }),
  });
  if (!response.ok) throw await toError(response);

  const payload = await response.json();
  return payload._embedded.redaction_candidates as CandidateReport;
}

/**
 * Fetch a rendered page as an object URL.
 *
 * The page image carries unredacted content, so it is returned as bytes
 * behind the caller's own credentials rather than as a shareable signed URL.
 * Callers must revoke the object URL when they are finished with it.
 */
export async function fetchRedactionPageImage(
  recordId: string,
  page: number,
): Promise<string> {
  const response = await apiFetch(
    `/api/v1/records/${recordId}/redaction-pages/${page}`,
  );
  if (!response.ok) throw await toError(response);
  return URL.createObjectURL(await response.blob());
}

export async function createRedaction(
  href: string,
  payload: {
    selections: RedactionSelectionPayload[];
    public_interest_note: string | null;
    source_generation: string | null;
  },
): Promise<RedactionManifest> {
  const response = await apiFetch(href, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw await toError(response);

  const body = await response.json();
  return { ...body._embedded.redaction, _links: body._links } as RedactionManifest;
}

export async function fetchRedactions(href: string): Promise<RedactionManifest[]> {
  const response = await apiFetch(href);
  if (!response.ok) throw await toError(response);

  const payload = await response.json();
  return (payload._embedded?.redactions ?? []) as RedactionManifest[];
}

/** Resolve the signed download URL for a redacted copy. */
export async function resolveRedactionDownloadUrl(href: string): Promise<string> {
  const response = await apiFetch(href);
  if (!response.ok) throw await toError(response);

  const payload = await response.json();
  if (!payload.downloadUrl) {
    throw new RedactionRequestError('No download URL was returned', 500, null);
  }
  return payload.downloadUrl as string;
}
